import type { RequestBody } from '@vrplatform/data';
import { bankersRound } from '@vrplatform/utils';

type CreateBankTransferRequestBody = RequestBody<'post:/transactions'>;
type UpdateBankTransferRequestBody = RequestBody<'put:/transactions/{id}'>;

export type EditableBankTransfer = {
  id: string;
  lineId?: string | null;
  fromBankAccountId: string | null;
  toBankAccountId: string | null;
  date: string | null;
  amount: number | '';
  description: string;
};

type EditableBankTransferLine = {
  id: string;
  amount?: number | null;
  centTotal?: number | null;
  account?: {
    id?: string | null;
  } | null;
};

export type EditableBankTransferSource = {
  id: string;
  date?: string | null;
  paidAt?: string | null;
  amount?: number | null;
  centTotal?: number | null;
  description?: string | null;
  account?: {
    id?: string | null;
  } | null;
  bankAccount?: {
    id?: string | null;
  } | null;
  payment?: {
    date?: string | null;
  } | null;
  toBankAccount?: {
    id?: string | null;
  } | null;
  lines?: EditableBankTransferLine[] | null;
};

export const buildAddBankTransferRequestBody = (args: {
  fromBankAccountId: string;
  toBankAccountId: string;
  date: string;
  amount: number;
  description: string | undefined;
}): CreateBankTransferRequestBody => {
  return {
    accountId: args.fromBankAccountId,
    date: args.date,
    description: args.description,
    type: 'transfer',
    payment: {
      status: 'paid',
      date: args.date,
    },
    lines: [
      {
        accountId: args.toBankAccountId,
        amount: bankersRound(args.amount * -1, 0),
        assignment: 'payout_distribution',
        description: args.description || 'Bank transfer',
      },
    ],
  };
};

export const buildEditBankTransferRequestBody = (args: {
  lineId: string;
  fromBankAccountId: string;
  toBankAccountId: string;
  date: string;
  amount: number;
  description: string | undefined;
}): UpdateBankTransferRequestBody => {
  return {
    accountId: args.fromBankAccountId,
    date: args.date,
    description: args.description,
    payment: {
      date: args.date,
    },
    lines_update: [
      {
        id: args.lineId,
        accountId: args.toBankAccountId,
        amount: bankersRound(args.amount * -1, 0),
        assignment: 'payout_distribution',
      },
    ],
  };
};

export const getEditableBankTransfer = (
  bankTransfer: EditableBankTransferSource
): EditableBankTransfer => {
  const fromBankAccountId =
    bankTransfer.account?.id ?? bankTransfer.bankAccount?.id ?? null;
  const counterpartyLine =
    bankTransfer.lines?.find(
      (line) => line.account?.id !== fromBankAccountId
    ) ??
    bankTransfer.lines?.find((line) => line.account?.id) ??
    bankTransfer.lines?.[0];

  return {
    id: bankTransfer.id,
    lineId: counterpartyLine?.id ?? null,
    fromBankAccountId,
    toBankAccountId:
      counterpartyLine?.account?.id ?? bankTransfer.toBankAccount?.id ?? null,
    date:
      bankTransfer.payment?.date ??
      bankTransfer.paidAt ??
      bankTransfer.date ??
      null,
    description: bankTransfer.description ?? '',
    amount:
      typeof bankTransfer.amount === 'number'
        ? Math.abs(bankTransfer.amount)
        : typeof bankTransfer.centTotal === 'number'
          ? Math.abs(bankTransfer.centTotal)
          : Math.abs(
              counterpartyLine?.amount ?? counterpartyLine?.centTotal ?? 0
            ),
  };
};
